/**
 * TanStack Query hooks for attachments. Multipart uploads bypass our
 * JSON-only apiCall wrapper so we use fetch directly here.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Attachment } from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { downscaleImage } from '@/lib/imageDownscale';
import { env } from '@/lib/env';

type ListResponse = { data: Attachment[] };

function key(punchItemId: string) {
  return ['punch-items', punchItemId, 'attachments'] as const;
}

export function usePunchItemAttachments(punchItemId: string | undefined) {
  return useQuery<ListResponse>({
    queryKey: key(punchItemId ?? ''),
    enabled: !!punchItemId,
    queryFn: () =>
      apiCall<ListResponse>(`/punch-items/${punchItemId}/attachments`),
  });
}

export function useUploadPunchItemPhoto(punchItemId: string) {
  const qc = useQueryClient();
  return useMutation<Attachment, Error, { file: File }>({
    // Route through apiCall so FormData + the real auth-header provider
    // (MSAL bearer or mock X-Mock-User) flow through — same as the task
    // uploader below. A previous raw fetch sent only X-Mock-User, which
    // failed under real Entra auth ("Missing bearer token").
    mutationFn: async ({ file }) => {
      const fd = new FormData();
      fd.append('file', await downscaleImage(file));
      return apiCall<Attachment>(`/punch-items/${punchItemId}/attachments`, {
        method: 'POST',
        body: fd,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(punchItemId) });
    },
  });
}

// ---- Task-level photos (Pillar B.3) ----------------------------------
// Unlike the punch uploader above, these go through apiCall (which passes
// FormData and the real auth-header provider) so they work in both mock
// and MSAL modes.

function taskKey(taskId: string) {
  return ['tasks', taskId, 'attachments'] as const;
}

export function useTaskAttachments(taskId: string | undefined) {
  return useQuery<ListResponse>({
    queryKey: taskKey(taskId ?? ''),
    enabled: !!taskId,
    queryFn: () => apiCall<ListResponse>(`/tasks/${taskId}/attachments`),
  });
}

export function useUploadTaskMedia(taskId: string) {
  const qc = useQueryClient();
  return useMutation<Attachment, Error, { file: File; noteId?: string | null }>({
    mutationFn: async ({ file, noteId }) => {
      const fd = new FormData();
      // note_id must precede the file part so the server parses it before
      // consuming the file stream (groups this media under the note).
      if (noteId) fd.append('note_id', noteId);
      fd.append('file', await downscaleImage(file));
      return apiCall<Attachment>(`/tasks/${taskId}/attachments`, { method: 'POST', body: fd });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKey(taskId) });
    },
  });
}

/** Upload media pinned to a punch item (dynamic id — created at submit time). */
export async function uploadPunchItemMedia(punchItemId: string, file: File): Promise<Attachment> {
  const fd = new FormData();
  fd.append('file', await downscaleImage(file));
  return apiCall<Attachment>(`/punch-items/${punchItemId}/attachments`, {
    method: 'POST',
    body: fd,
  });
}

/**
 * Upload a file (image, PDF, or video) to a task by id — used right after a task
 * is created, when no bound hook exists yet (create-time attachments, demo #7).
 * Images are downscaled; PDFs/videos pass through untouched. The task endpoint
 * accepts PDFs (allowPdf), so a spec/cut sheet can ride along with the task.
 */
export async function uploadTaskMedia(taskId: string, file: File): Promise<Attachment> {
  const fd = new FormData();
  fd.append('file', await downscaleImage(file));
  return apiCall<Attachment>(`/tasks/${taskId}/attachments`, { method: 'POST', body: fd });
}

export function useDeleteAttachment(punchItemId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiCall<void>(`/attachments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(punchItemId) });
    },
  });
}

// ---- Project intake documents (IH.A / IH.6) --------------------------
// Source paperwork (sales order / packing slip / design package / other)
// persisted at project level by the intake flows after the project is created.

type IntakeDocKind = 'sales_order' | 'packing_slip' | 'design_package' | 'other';

function projectDocsKey(projectId: string) {
  return ['projects', projectId, 'documents'] as const;
}

export function useProjectDocuments(projectId: string | undefined) {
  return useQuery<ListResponse>({
    queryKey: projectDocsKey(projectId ?? ''),
    enabled: !!projectId,
    queryFn: () => apiCall<ListResponse>(`/projects/${projectId}/documents`),
  });
}

/**
 * Upload a source document (PDF or image) to a project. Optional `kind` (the
 * server guesses from the filename when omitted), `extractedMetadata` (the AI
 * pipeline's structured extract), and `pages` (design-package classification).
 * Fields precede the file part so the server parses them before the stream.
 */
export async function uploadProjectDocument(opts: {
  projectId: string;
  file: File;
  kind?: IntakeDocKind;
  extractedMetadata?: Record<string, unknown> | null;
  pages?: unknown;
  /** Ask the server to classify + extract from the file's content (PDF only). */
  analyze?: boolean;
}): Promise<Attachment> {
  const fd = new FormData();
  if (opts.kind) fd.append('kind', opts.kind);
  if (opts.analyze) fd.append('analyze', 'true');
  if (opts.extractedMetadata) {
    fd.append('extracted_metadata', JSON.stringify(opts.extractedMetadata));
  }
  if (opts.pages) fd.append('pages', JSON.stringify(opts.pages));
  fd.append('file', opts.file);
  return apiCall<Attachment>(`/projects/${opts.projectId}/documents`, {
    method: 'POST',
    body: fd,
  });
}

/** URL that the <img> tag uses to load the attachment bytes. */
/**
 * URL for an attachment's bytes. Prefer the server-minted signed `file_url`
 * (the bare route now requires auth or a signature — audit M2); the plain
 * path remains as a fallback for code paths that only hold an id, where the
 * service worker's cached copy or an authed fetch may still serve it.
 */
export function attachmentFileUrl(att: string | { id: string; file_url?: string }): string {
  if (typeof att === 'string') return `${env.API_BASE_URL}/attachments/${att}/file`;
  return att.file_url ?? `${env.API_BASE_URL}/attachments/${att.id}/file`;
}
